import { Tracker } from '@/core/classes/Model'
import _ from 'lodash'
import { SnackbarMessage, useSnackbar } from 'notistack'
import { useCallback } from 'react'
import { RootState } from '@/redux/rootReducer'
import { checkIfObjectId } from '@/utils/helpers'
import { useRequestHandler } from './useRequestHandler'

/**
 * Interface to force the object to have the _id property
 */
interface hasId {
  _id: string
}

export type checkConfig<T extends hasId> = {
  /**
   * Success snackbar message
   */
  successMsg: SnackbarMessage
  /**
   * error snackbar message
   */
  errorMsg: SnackbarMessage
  /**
   * Tracker for the create action so we know when the item is created
   */
  itemTracker: (state: RootState) => Tracker
  /**
   * The create item action that will be dispatched
   */
  createAction: (item: T) => { payload: any }
  /**
   * Last ID selector so we have the ID of the item just created
   */
  latestIDSelector: string | undefined
  /**
   * Callback function that is called once the item is created or if it already exists
   */
  callback: (item: T) => void
  /**
   * Item list used to find the Entity from the Entity ID given to the function
   */
  itemList: T[]
}

/**
 * Hook with a logic to create an item if its doesnt match the objectID regex (the object doesn't exist in the DB)
 * @param config  the config object for the hook
 * @returns checkItem function (give it the id of the item of type T)
 */
export function useCheckIfExists<T extends hasId>(config: checkConfig<T>): (item: string) => void {
  const { enqueueSnackbar } = useSnackbar()

  const getItem = useCallback(
    (itemId: string) => {
      return _.find(config.itemList, (item) => item._id === itemId)
    },
    [config.itemList]
  )

  const reqHandler = useRequestHandler({
    onSuccess: () => {
      enqueueSnackbar(config.successMsg, { variant: 'success' })
      if (config.latestIDSelector) {
        checkItem(config.latestIDSelector!)
      }
    },
    onError: () => {
      enqueueSnackbar(config.errorMsg, { variant: 'error' })
    },
    requestTrackerSelector: config.itemTracker,
  })

  /**
   * checkItem function, takes EntryID which is an ID of an object from the redux store
   * calls the callback function once its done
   */
  const checkItem = useCallback(
    (entryId: string) => {
      const item = getItem(entryId)

      if (!item) return

      if (checkIfObjectId(entryId)) {
        config.callback(item)
      } else {
        reqHandler.dispatch(config.createAction(item))
      }
    },
    [config, getItem, reqHandler]
  )

  return checkItem
}
